import { Injectable } from '@angular/core';
import {
	ActivatedRouteSnapshot,
	CanActivateChild,
	Router,
	UrlTree,
} from '@angular/router';
import { DeploymentContext } from '../utilities/deployment-context/deployment-context.js';
import { Logger } from '../utilities/logger/logger.js';

/** The one route users are allowed to reach when the
 *  restrictToSearchResultsPage feature switch is enabled.
 */
export const SEARCH_RESULTS_ROUTE = '/search-results';

/** Route data key marking a route as still reachable in restricted mode.
 *  Anything not marked redirects to the Search Results page, so a newly added
 *  route is restricted by default rather than by omission.
 */
export const AVAILABLE_WHEN_RESTRICTED = 'availableWhenRestricted';

@Injectable({ providedIn: 'root' })
/*
  Implements the restrictToSearchResultsPage feature switch: when it is on, the
  application collapses down to the Search Results page and the routes the login
  flow needs to get a user there. Everything else redirects to /search-results.

  This is registered as a canActivateChild on the root route so that it applies
  to every route without each one having to opt in, and so that it runs ahead of
  the individual routes' canActivate guards.

  When the switch is off this guard is a no-op and routing behaves as it always has.
*/
export class RestrictToSearchResultsRouteGuard implements CanActivateChild {
	protected logger = new Logger(this.constructor.name);

	constructor(
		private router: Router,
		private _deploymentContext: DeploymentContext
	) {}

	public canActivateChild(route: ActivatedRouteSnapshot): boolean | UrlTree {
		if (
			!this._deploymentContext.featureSwitches
				?.restrictToSearchResultsPage
		) {
			return true;
		}

		if (route.data?.[AVAILABLE_WHEN_RESTRICTED] === true) {
			return true;
		}

		this.logger.warn(
			`Redirecting to ${SEARCH_RESULTS_ROUTE} because the restrictToSearchResultsPage feature switch is enabled.`
		);

		// Preserve the query string, since the Search Results page uses it for
		// deep linked search predicates, filters and sorts.
		return this.router.createUrlTree([SEARCH_RESULTS_ROUTE], {
			queryParams: route.queryParams,
		});
	}
}
